import { Maximize2 } from 'lucide-react'
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

export interface ProductDailyData {
  date: string
  spend: number
  roas: number
  acqRoas?: number
}

interface ProductChartCardProps {
  productName: string
  productCode?: string
  data: ProductDailyData[]
  onExpand?: () => void
}

const formatCurrency = (value: number) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`
  return `$${value.toFixed(0)}`
}

const formatDate = (date: string) => {
  const d = new Date(date)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{
    name: string
    value: number
    color: string
    dataKey: string
  }>
  label?: string
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null

  return (
    <div className="bg-tf-bg-card border border-tf-border rounded-lg shadow-lg p-2 text-xs">
      <p className="font-medium text-tf-text mb-1">{formatDate(label || '')}</p>
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-1.5">
          <div
            className="w-2 h-2 rounded-sm"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-tf-text-muted">{entry.name}:</span>
          <span className="font-medium text-tf-text">
            {entry.dataKey === 'spend' 
              ? formatCurrency(entry.value)
              : entry.value.toFixed(2)
            }
          </span>
        </div>
      ))}
    </div>
  )
}

export function ProductChartCard({ productName, productCode, data, onExpand }: ProductChartCardProps) {
  if (!data.length) {
    return (
      <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-tf-text truncate">{productName}</h3>
        </div>
        <div className="h-40 flex items-center justify-center text-tf-text-muted text-sm">
          No data
        </div>
      </div>
    )
  }

  const maxSpend = Math.max(...data.map(d => d.spend)) * 1.1 || 1
  const maxRoas = Math.max(...data.map(d => Math.max(d.roas, d.acqRoas || 0))) * 1.1 || 2

  return (
    <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-sm font-semibold text-tf-text truncate">{productName}</h3>
          {productCode && (
            <span className="text-xs text-tf-text-muted flex-shrink-0">{productCode}</span>
          )}
        </div>
        {onExpand && (
          <button
            onClick={onExpand}
            className="p-1 rounded hover:bg-tf-bg-hover transition-colors flex-shrink-0"
          >
            <Maximize2 className="w-3.5 h-3.5 text-tf-text-muted" />
          </button>
        )}
      </div>
      
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 9, fill: '#9ca3af' }}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              tickFormatter={formatCurrency}
              tick={{ fontSize: 9, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              domain={[0, maxSpend]}
              width={35}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 9, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              domain={[0, maxRoas]}
              width={25}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              yAxisId="left"
              dataKey="spend"
              name="Spend"
              fill="#99d9d9"
              radius={[1, 1, 0, 0]}
              barSize={8}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="roas"
              name="ROAS"
              stroke="#3b82f6"
              strokeWidth={1.5}
              dot={false}
            />
            {data.some(d => d.acqRoas !== undefined) && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="acqRoas"
                name="Acq ROAS"
                stroke="#8b5cf6"
                strokeWidth={1.5}
                dot={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-2 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-2 bg-[#99d9d9] rounded-sm" />
          <span className="text-tf-text-muted">Spend</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-0.5 bg-[#3b82f6]" />
          <span className="text-tf-text-muted">ROAS</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-0.5 bg-[#8b5cf6]" />
          <span className="text-tf-text-muted">Acq ROAS</span>
        </div>
      </div>
    </div>
  )
}

export default ProductChartCard
